import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  apiAdminStats, apiAdminUsers, apiAdminProfiles, apiAdminChats,
  apiAdminToggleUser, apiAdminDeleteUser, apiAdminDeleteProfile,
  apiAdminUserDetail, apiAdminChatMessages,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Users, Heart, MessageCircle, BookOpen, LogOut,
  Trash2, Ban, Eye, ChevronLeft, Search, RefreshCw, CreditCard,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Panoramica", icon: Shield },
  { id: "users", label: "Utenti", icon: Users },
  { id: "profiles", label: "Profili AI", icon: Heart },
  { id: "chats", label: "Chat", icon: MessageCircle },
];

export default function AdminDashboardPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [profiles, setProfiles] = useState({ profiles: [], total: 0 });
  const [chats, setChats] = useState({ sessions: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [chatDetail, setChatDetail] = useState(null);

  const token = localStorage.getItem("admin_token");

  const load = useCallback(async () => {
    if (!token) { nav("/admin/login"); return; }
    setLoading(true);
    try {
      const [s, u, p, c] = await Promise.all([
        apiAdminStats(), apiAdminUsers(), apiAdminProfiles(), apiAdminChats(),
      ]);
      setStats(s);
      setUsers(u);
      setProfiles(p);
      setChats(c);
    } catch {
      toast.error("Sessione scaduta");
      localStorage.removeItem("admin_token");
      nav("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [token, nav]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };

  const handleToggleUser = async (userId) => {
    try {
      const res = await apiAdminToggleUser(userId);
      toast.success(res.disabled ? "Utente disattivato" : "Utente riattivato");
      load();
    } catch { toast.error("Errore"); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Eliminare questo utente e tutti i suoi dati?")) return;
    try {
      await apiAdminDeleteUser(userId);
      toast.success("Utente eliminato");
      setDetail(null);
      load();
    } catch { toast.error("Errore"); }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm("Eliminare questo profilo e tutte le chat?")) return;
    try {
      await apiAdminDeleteProfile(profileId);
      toast.success("Profilo eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const viewUser = async (userId) => {
    try {
      const d = await apiAdminUserDetail(userId);
      setDetail(d);
    } catch { toast.error("Errore"); }
  };

  const viewChat = async (profileId) => {
    try {
      const d = await apiAdminChatMessages(profileId);
      setChatDetail(d);
    } catch { toast.error("Errore"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(220_25%_8%)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filteredUsers = users.users.filter((u) =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[hsl(220_25%_8%)] flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-white/8 bg-[hsl(220_20%_10%)] flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-white/8">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent" />
            <span className="font-serif text-base font-bold text-white">IoCiSono</span>
          </div>
          <p className="text-[10px] text-white/30 mt-1 tracking-wider uppercase">Super Admin</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => { setTab(t.id); setDetail(null); setChatDetail(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                tab === t.id ? "bg-accent/15 text-accent" : "text-white/50 hover:text-white/80 hover:bg-white/5"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/8">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-6xl">
          {/* Overview Tab */}
          {tab === "overview" && stats && (
            <div data-testid="admin-overview">
              <div className="flex items-center justify-between mb-8">
                <h1 className="font-serif text-2xl font-bold text-white">Panoramica</h1>
                <Button variant="ghost" size="sm" onClick={load} className="text-white/40 hover:text-white">
                  <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Utenti totali" value={stats.totals.users} icon={Users} color="text-blue-400" />
                <StatCard label="Profili AI" value={stats.totals.profiles} icon={Heart} color="text-rose-400" />
                <StatCard label="Messaggi Chat" value={stats.totals.messages} icon={MessageCircle} color="text-emerald-400" />
                <StatCard label="Pre-ordini pagati" value={stats.preorders?.paid || 0} icon={CreditCard} color="text-amber-400" />
              </div>
              {/* Pre-order Revenue */}
              {stats.preorders && (
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                  <Card className="bg-white/5 border-white/8">
                    <CardContent className="p-5">
                      <p className="text-xs text-white/40 mb-1">Revenue Pre-ordini</p>
                      <p className="text-2xl font-bold text-emerald-400">{stats.preorders.revenue}€</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/5 border-white/8">
                    <CardContent className="p-5">
                      <p className="text-xs text-white/40 mb-1">Checkout iniziati</p>
                      <p className="text-2xl font-bold text-white">{stats.preorders.total}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/5 border-white/8">
                    <CardContent className="p-5">
                      <p className="text-xs text-white/40 mb-1">Conversion rate</p>
                      <p className="text-2xl font-bold text-white">
                        {stats.preorders.total > 0 ? Math.round((stats.preorders.paid / stats.preorders.total) * 100) : 0}%
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="bg-white/5 border-white/8">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-medium text-white/50 mb-4">Nuovi utenti (7 giorni)</h3>
                    <p className="text-3xl font-bold text-white">{stats.recent_users_7d}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/8">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-medium text-white/50 mb-4">Distribuzione account</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Per me (Diario)</span>
                        <span className="text-white font-medium">{stats.account_types.per_me}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Rivivi un rapporto</span>
                        <span className="text-white font-medium">{stats.account_types.rivivi}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Non impostato</span>
                        <span className="text-white/40 font-medium">{stats.account_types.non_impostato}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {tab === "users" && !detail && (
            <div data-testid="admin-users">
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif text-2xl font-bold text-white">Utenti ({users.total})</h1>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    placeholder="Cerca utente..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="admin-user-search"
                  />
                </div>
              </div>
              <div className="space-y-2">
                {filteredUsers.map((u) => (
                  <Card key={u.id} className="bg-white/5 border-white/8 hover:bg-white/8 transition-colors">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
                          <span className="text-sm font-bold text-accent">{u.name?.[0]?.toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{u.name}</p>
                          <p className="text-xs text-white/40">{u.email}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          u.account_type === "rivivi" ? "bg-rose-500/15 text-rose-400"
                            : u.account_type === "per_me" ? "bg-blue-500/15 text-blue-400"
                            : "bg-white/10 text-white/30"
                        }`}>
                          {u.account_type || "—"}
                        </span>
                        {u.disabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">disattivato</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => viewUser(u.id)} className="text-white/30 hover:text-white h-8 w-8" data-testid={`admin-view-user-${u.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleToggleUser(u.id)} className="text-white/30 hover:text-amber-400 h-8 w-8">
                          <Ban className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} className="text-white/30 hover:text-red-400 h-8 w-8">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {filteredUsers.length === 0 && <p className="text-sm text-white/30 text-center py-8">Nessun utente trovato</p>}
              </div>
            </div>
          )}

          {/* User Detail */}
          {tab === "users" && detail && (
            <div data-testid="admin-user-detail">
              <button onClick={() => setDetail(null)} className="flex items-center gap-2 text-sm text-white/40 hover:text-white mb-6 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Torna alla lista
              </button>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="font-serif text-2xl font-bold text-white">{detail.user.name}</h1>
                  <p className="text-sm text-white/40">{detail.user.email}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(detail.user.id)} className="text-red-400/60 hover:text-red-400">
                  <Trash2 className="w-4 h-4 mr-2" /> Elimina
                </Button>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <Card className="bg-white/5 border-white/8">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-white">{detail.profiles.length}</p>
                    <p className="text-xs text-white/40">Profili</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/8">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-white">{detail.total_messages}</p>
                    <p className="text-xs text-white/40">Messaggi</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/8">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-white">{detail.diary_entries_count}</p>
                    <p className="text-xs text-white/40">Voci diario</p>
                  </CardContent>
                </Card>
              </div>
              {detail.profiles.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-white/50 mb-3">Profili</h3>
                  <div className="space-y-2">
                    {detail.profiles.map((p) => (
                      <Card key={p.id} className="bg-white/5 border-white/8">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-white">{p.nome} {p.soprannome ? `"${p.soprannome}"` : ""}</p>
                            <p className="text-xs text-white/40 capitalize">{p.relazione} | {p.photos?.length || 0} foto | {p.audio_files?.length || 0} audio</p>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "ready" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{p.status}</span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profiles Tab */}
          {tab === "profiles" && (
            <div data-testid="admin-profiles">
              <h1 className="font-serif text-2xl font-bold text-white mb-6">Profili AI ({profiles.total})</h1>
              <div className="space-y-2">
                {profiles.profiles.map((p) => (
                  <Card key={p.id} className="bg-white/5 border-white/8">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-rose-500/10 flex items-center justify-center">
                          <Heart className="w-4 h-4 text-rose-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{p.nome} {p.soprannome ? `"${p.soprannome}"` : ""}</p>
                          <p className="text-xs text-white/40 capitalize">{p.relazione} | Utente: {p.user_id?.slice(0, 8)}...</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "ready" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{p.status}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteProfile(p.id)} className="text-white/30 hover:text-red-400 h-8 w-8">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                {profiles.profiles.length === 0 && <p className="text-sm text-white/30 text-center py-8">Nessun profilo</p>}
              </div>
            </div>
          )}

          {/* Chats Tab */}
          {tab === "chats" && !chatDetail && (
            <div data-testid="admin-chats">
              <h1 className="font-serif text-2xl font-bold text-white mb-6">Sessioni Chat ({chats.total})</h1>
              <div className="space-y-2">
                {chats.sessions.map((s) => (
                  <Card key={s.profile_id} className="bg-white/5 border-white/8 hover:bg-white/8 transition-colors cursor-pointer" onClick={() => viewChat(s.profile_id)}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                          <MessageCircle className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{s.profile_name} <span className="text-white/30 capitalize">({s.relazione})</span></p>
                          <p className="text-xs text-white/40">{s.message_count} messaggi | Ultimo: {new Date(s.last_message).toLocaleDateString("it-IT")}</p>
                        </div>
                      </div>
                      <Eye className="w-4 h-4 text-white/20" />
                    </CardContent>
                  </Card>
                ))}
                {chats.sessions.length === 0 && <p className="text-sm text-white/30 text-center py-8">Nessuna chat</p>}
              </div>
            </div>
          )}

          {/* Chat Detail */}
          {tab === "chats" && chatDetail && (
            <div data-testid="admin-chat-detail">
              <button onClick={() => setChatDetail(null)} className="flex items-center gap-2 text-sm text-white/40 hover:text-white mb-6 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Torna alle chat
              </button>
              <h1 className="font-serif text-2xl font-bold text-white mb-2">
                Chat con {chatDetail.profile?.nome}
              </h1>
              <p className="text-xs text-white/40 mb-6 capitalize">{chatDetail.profile?.relazione}</p>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                {chatDetail.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${
                      m.role === "user" ? "bg-accent/20 text-white rounded-br-md" : "bg-white/5 text-white/80 rounded-bl-md"
                    }`}>
                      <p className="whitespace-pre-wrap">{m.content}</p>
                      <p className="text-[10px] mt-1 opacity-40">
                        {new Date(m.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card className="bg-white/5 border-white/8">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-white/40 mt-1">{label}</p>
      </CardContent>
    </Card>
  );
}
